import { computed, onBeforeUnmount, ref } from "vue";
import { useField, useFormContext } from "vee-validate";
import { useI18n } from "vue-i18n";

import type { TFile } from "@/api/shared/file";
import {
  buildFileAccept,
  EFileType,
  getExtension,
  getFileType,
  isFileTooLarge,
  MAX_FILE_SIZE_BYTES,
  useFile,
} from "@/composables/shared/useFile";
import { useSnackStore } from "@/stores/snackStore";
import { djangoErrorHandler } from "@/utils/DjangoError";
import { getFileSize } from "@/utils/Formatters";

export type TUploadFieldModel = TFile | TFile[] | null;

export type TUploadFieldSharedProps = {
  name: string;
  label?: string;
  disabled?: boolean;
  optional?: boolean;
  multiple?: boolean;
  maxFiles?: number | null;
  acceptType?: EFileType | EFileType[];
  /**
   * Extension allowlist (without the leading dot), checked instead of
   * `acceptType` when set. Use when a field needs a subset of a type bucket —
   * e.g. PDFs but not the other office formats `EFileType.DOCUMENT` covers.
   */
  acceptExtensions?: string[];
  icon?: string;
  imgSrc?: string;
  /** Renders imgSrc as a theme-colored mask (for black SVG slot diagrams). */
  themeAwareIcon?: boolean;
  variant?: "default" | "avatar";
  waitingForBackend?: boolean;
  enableImageEditor?: boolean;
  editorAspectRatio?: number;
  editorInline?: boolean;
  editorMaxWidth?: number;
  editorMaxHeight?: number;
  disabledFiles?: TFile[];
  overlayActions?: boolean;
};

export type TUploadItem = {
  id: string;
  localFile: File | null;
  remoteFile: TFile;
  originalName: string;
};

type TUploadJob = {
  itemId: string;
  file: File;
};

export function useUploadFieldBase(props: TUploadFieldSharedProps) {
  const { t } = useI18n();
  const snackStore = useSnackStore();
  const formContext = useFormContext();

  const setErrors = formContext?.setErrors ?? (() => undefined);

  const { uploadFileWithProgress, uploadProgress, removeProgress, clearAllProgress } = useFile();

  const { value, errorMessage, setValue, setTouched } = useField<TUploadFieldModel>(
    () => props.name ?? "",
    undefined,
    {
      keepValueOnUnmount: true,
    }
  );

  const uploadItems = ref<TUploadItem[]>([]);

  const isLoading = ref(false);
  const isUploading = ref(false);

  const editorOriginalFile = ref<File | null>(null);
  const originalUploadedFile = ref<File | null>(null);

  const fileInput = ref<HTMLInputElement | null>(null);
  const isDragOver = ref(false);
  const isHovered = ref(false);
  const dragCounter = ref(0);

  const createdObjectUrls = new Set<string>();
  const disabledFileIds = computed(() => new Set((props.disabledFiles ?? []).map((f) => f.id)));

  const uploadedFiles = computed<TFile[]>(() => uploadItems.value.map((item) => item.remoteFile));

  const localFiles = computed<File[]>(() =>
    uploadItems.value.flatMap((item) => (item.localFile ? [item.localFile] : []))
  );

  const isBusy = computed(
    () => !!props.disabled || !!props.waitingForBackend || isLoading.value || isUploading.value
  );

  function createStableId() {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return crypto.randomUUID();
    }

    return `upload-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  function handleError(error: unknown) {
    console.error("[UploadField]", error);
    djangoErrorHandler(snackStore, setErrors)(error);
  }

  function showUploadSuccess(count = 1) {
    snackStore.showSuccess(
      t("generic.messages.success.upload", {
        entity: t("generic.entity.file", count > 1 ? 2 : 1),
      })
    );
  }

  function syncFieldValue(shouldValidate = true, markTouched = false) {
    const files = uploadedFiles.value;
    const newValue = props.multiple ? files : (files.at(0) ?? null);

    setValue(newValue, shouldValidate);
    if (markTouched) {
      setTouched(true);
    }
  }

  function revokeObjectUrl(url?: string | null) {
    if (!url || !url.startsWith("blob:") || !createdObjectUrls.has(url)) {
      return;
    }

    URL.revokeObjectURL(url);
    createdObjectUrls.delete(url);
  }

  function revokeItemObjectUrls(item: TUploadItem) {
    revokeObjectUrl(item.remoteFile.file);
    revokeObjectUrl(item.remoteFile.localUrl);
  }

  function createTempRemoteFile(file: File, itemId: string): TFile {
    const objectUrl = URL.createObjectURL(file);
    createdObjectUrls.add(objectUrl);

    return {
      id: `temp-${itemId}`,
      name: file.name,
      original_name: file.name,
      file: objectUrl,
      created_at: new Date(),
      is_assigned: false,
      mimetype: file.type,
      size: file.size,
      isUploading: true,
      localUrl: objectUrl,
    } as TFile;
  }

  function createUploadItem(file: File): TUploadItem {
    const id = createStableId();

    return {
      id,
      localFile: file,
      originalName: file.name,
      remoteFile: createTempRemoteFile(file, id),
    };
  }

  function createPlaceholderLocalFile(currentFile: TFile): File {
    const displayName = currentFile.original_name || currentFile.name;

    return new File([new Blob([], { type: currentFile.mimetype })], displayName, {
      type: currentFile.mimetype,
      lastModified: new Date(currentFile.created_at as string | number | Date).getTime(),
    });
  }

  function createUploadItemFromRemoteFile(currentFile: TFile): TUploadItem {
    return {
      id: createStableId(),
      originalName: currentFile.original_name || currentFile.name,
      localFile: createPlaceholderLocalFile(currentFile),
      remoteFile: currentFile,
    };
  }

  function findUploadItemIndex(file: TFile) {
    return uploadItems.value.findIndex(
      (item) => item.remoteFile === file || item.remoteFile.id === file.id
    );
  }

  function findUploadItem(file: TFile) {
    const index = findUploadItemIndex(file);
    return index === -1 ? null : uploadItems.value[index]!;
  }

  function isFileDisabled(file: TFile) {
    const id = file.id ?? findUploadItem(file)?.remoteFile.id;
    return !!id && disabledFileIds.value.has(id);
  }

  function isAcceptedFileType(file: File): boolean {
    // An explicit extension list wins, and is matched on the filename rather
    // than the mimetype: `getFileType` reports e.g. a picked `.pdf` as
    // APPLICATION, so a type-bucket check would reject it.
    if (props.acceptExtensions?.length) {
      const extension = getExtension(file.name);
      return !!extension && props.acceptExtensions.includes(extension);
    }

    if (!props.acceptType) return true;

    const acceptedFileTypes = Array.isArray(props.acceptType)
      ? props.acceptType
      : [props.acceptType];

    return acceptedFileTypes.includes(getFileType(file));
  }

  function getAcceptedTypesLabel() {
    if (props.acceptExtensions?.length) {
      return props.acceptExtensions.map((extension) => `.${extension}`).join(" ");
    }

    const acceptedTypesArray = Array.isArray(props.acceptType)
      ? props.acceptType
      : props.acceptType
        ? [props.acceptType]
        : [];

    return acceptedTypesArray.map((type) => t(`generic.file.acceptedType.${type}`)).join(", ");
  }

  const acceptAttribute = computed(
    () =>
      buildFileAccept({ extensions: props.acceptExtensions, types: props.acceptType }) ?? undefined
  );

  function validateFileType(file: File): boolean {
    if (isAcceptedFileType(file)) {
      return true;
    }

    snackStore.showError(
      t("generic.file.error.invalidFileType", {
        fileName: file.name,
        acceptedTypes: getAcceptedTypesLabel(),
      }),
      false
    );

    return false;
  }

  function validateFileSize(file: File): boolean {
    if (!isFileTooLarge(file)) {
      return true;
    }

    snackStore.showError(
      t("generic.file.error.invalidFileSize", {
        fileName: file.name,
        maxSize: getFileSize(MAX_FILE_SIZE_BYTES),
      }),
      false
    );

    return false;
  }

  function validateSelectedFile(file: File): boolean {
    return validateFileType(file) && validateFileSize(file);
  }

  async function uploadToBackend(file: File, progressKey: string): Promise<TFile> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", file.name);

    const response = await uploadFileWithProgress(formData, progressKey, file.name);

    return response.data;
  }

  function clearUploadItems() {
    for (const item of uploadItems.value) {
      revokeItemObjectUrls(item);
      removeProgress(item.id);
    }

    uploadItems.value = [];
  }

  async function uploadMultipleFiles(selectedFiles: File[]) {
    if (!selectedFiles.length || isBusy.value) return;

    // Validate before touching what is already there: in single mode the
    // replacement purge below is destructive, so a rejected pick must leave the
    // current file intact rather than emptying the field behind the error snack.
    const acceptedFiles = selectedFiles.filter((selectedFile) =>
      validateSelectedFile(selectedFile)
    );

    if (acceptedFiles.length === 0) {
      syncFieldValue(true, true);
      return;
    }

    if (!props.multiple) {
      clearUploadItems();
    }

    const uploadJobs: TUploadJob[] = [];

    let availableSlots = props.multiple
      ? props.maxFiles != null
        ? Math.max(0, props.maxFiles - uploadItems.value.length)
        : null
      : Math.max(0, 1 - uploadItems.value.length);

    let maxFilesErrorShown = false;

    for (const selectedFile of acceptedFiles) {
      if (availableSlots !== null && availableSlots <= 0) {
        if (!maxFilesErrorShown) {
          snackStore.showError(
            t("generic.file.error.maxFilesReached", {
              max: props.multiple ? props.maxFiles : 1,
            }),
            false
          );
          maxFilesErrorShown = true;
        }
        continue;
      }

      if (availableSlots !== null) {
        availableSlots -= 1;
      }

      const item = createUploadItem(selectedFile);

      uploadItems.value.unshift(item);

      uploadJobs.push({
        itemId: item.id,
        file: selectedFile,
      });
    }

    if (!uploadJobs.length) {
      syncFieldValue(true, true);
      return;
    }

    syncFieldValue(true, true);
    isUploading.value = true;

    const results = await Promise.allSettled(
      uploadJobs.map(async (job) => {
        const uploaded = await uploadToBackend(job.file, job.itemId);
        const item = uploadItems.value.find((currentItem) => currentItem.id === job.itemId);

        if (item) {
          // Keep the device blob alive on `localUrl` so consumers can render the bytes
          // that are already in the browser instead of re-fetching the remote URL.
          // It is revoked on remove/unmount and stripped before submit.
          item.remoteFile = { ...uploaded, localUrl: item.remoteFile.localUrl };
        }

        return uploaded;
      })
    );

    let successCount = 0;

    results.forEach((result, index) => {
      const job = uploadJobs[index];
      if (!job) return;

      if (result.status === "fulfilled") {
        successCount += 1;
        return;
      }

      const itemIndex = uploadItems.value.findIndex((item) => item.id === job.itemId);
      if (itemIndex !== -1) {
        const item = uploadItems.value[itemIndex]!;
        revokeItemObjectUrls(item);
        uploadItems.value.splice(itemIndex, 1);
      }

      removeProgress(job.itemId);
      handleError(result.reason);
    });

    syncFieldValue(true, true);
    isUploading.value = false;

    if (successCount > 0) {
      showUploadSuccess(successCount);
    }
  }

  function onRemove(file: TFile) {
    const itemIndex = findUploadItemIndex(file);
    if (itemIndex === -1) return;

    const item = uploadItems.value[itemIndex]!;
    revokeItemObjectUrls(item);
    removeProgress(item.id);

    if (originalUploadedFile.value && item.localFile === originalUploadedFile.value) {
      originalUploadedFile.value = null;
    }

    if (editorOriginalFile.value && item.localFile === editorOriginalFile.value) {
      editorOriginalFile.value = null;
    }

    uploadItems.value.splice(itemIndex, 1);
    syncFieldValue(true, true);
  }

  async function initFromFieldValue() {
    clearUploadItems();
    originalUploadedFile.value = null;

    if (!value.value) {
      return;
    }

    try {
      isLoading.value = true;

      const currentFiles = Array.isArray(value.value) ? value.value : [value.value];
      uploadItems.value = currentFiles.map(createUploadItemFromRemoteFile);

      syncFieldValue(false);
    } catch (error) {
      handleError(error);
    } finally {
      isLoading.value = false;
    }
  }

  function openFileBrowser() {
    if (isBusy.value) return;

    if (fileInput.value) {
      fileInput.value.value = "";
      fileInput.value.click();
    }
  }

  function getDisplayFileSize(file: TFile) {
    const item = findUploadItem(file);

    const size =
      item?.localFile && item.localFile.size > 0 ? item.localFile.size : (file.size ?? 0);

    return getFileSize(size);
  }

  function onDragOver(event: DragEvent) {
    event.preventDefault();
    if (isBusy.value) return;
    isDragOver.value = true;
  }

  function onDragEnter(event: DragEvent) {
    event.preventDefault();
    if (isBusy.value) return;

    dragCounter.value += 1;
    isDragOver.value = true;
  }

  function onDragLeave(event: DragEvent) {
    event.preventDefault();
    if (isBusy.value) return;

    dragCounter.value = Math.max(0, dragCounter.value - 1);
    if (dragCounter.value === 0) {
      isDragOver.value = false;
    }
  }

  function onMouseEnter() {
    if (isBusy.value) return;
    isHovered.value = true;
  }

  function onMouseLeave() {
    isHovered.value = false;
  }

  function getUploadProgress(file: TFile) {
    const item = findUploadItem(file);
    if (!item) return null;

    return uploadProgress.value.get(item.id) ?? null;
  }

  onBeforeUnmount(() => {
    clearAllProgress();

    for (const url of Array.from(createdObjectUrls)) {
      revokeObjectUrl(url);
    }
  });

  return {
    t,
    acceptAttribute,
    errorMessage,
    value,
    uploadItems,
    uploadedFiles,
    localFiles,
    isLoading,
    isUploading,
    isBusy,
    editorOriginalFile,
    originalUploadedFile,
    fileInput,
    isDragOver,
    isHovered,
    getUploadProgress,
    initFromFieldValue,
    uploadMultipleFiles,
    onRemove,
    openFileBrowser,
    getDisplayFileSize,
    isFileDisabled,
    onDragOver,
    onDragEnter,
    onDragLeave,
    onMouseEnter,
    onMouseLeave,
  };
}
